if (!customElements.get('wls-features-image')) {
  class WLSFeaturesImage extends HTMLElement {
    constructor() {
      super();
      this.classes = {
        stack: 'js-wls-features-image__stack',
        hoverImg: 'js-wls-features-image__hover',
        stackHoverActive: 'wls-features-image__image-stack--hover-active',
      };

      this.attributesMap = {
        featureHoverImage: 'data-wls-feature-hover-image',
      };

      this._onFeatureEnter = this._onFeatureEnter.bind(this);
      this._onFeatureLeave = this._onFeatureLeave.bind(this);
      this._onFeaturePointerUp = this._onFeaturePointerUp.bind(this);
      this._onFeatureClick = this._onFeatureClick.bind(this);

      this._leaveTimeoutId = null;
      this._hoverGen = 0;
      this._stack = null;
      this._hoverImg = null;
      this._featureEls = [];
      this._clickActiveEl = null;
      this._lastTouchInteractionAt = 0;
      this._imgLoadHandler = null;
      this._imgErrorHandler = null;
    }

    _isTouchLikeInteraction() {
      return (
        window.matchMedia('(pointer: coarse)').matches ||
        window.matchMedia('(hover: none)').matches ||
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0
      );
    }

    _cleanupPendingLeaveTimeout() {
      if (this._leaveTimeoutId != null) {
        clearTimeout(this._leaveTimeoutId);
        this._leaveTimeoutId = null;
      }
    }

    _cleanupImageListeners() {
      if (!this._hoverImg) return;

      if (this._imgLoadHandler) {
        this._hoverImg.removeEventListener('load', this._imgLoadHandler);
        this._imgLoadHandler = null;
      }

      if (this._imgErrorHandler) {
        this._hoverImg.removeEventListener('error', this._imgErrorHandler);
        this._imgErrorHandler = null;
      }
    }

    connectedCallback() {
      this._stack = this.querySelector(`.${this.classes.stack}`);
      this._hoverImg = this.querySelector(`.${this.classes.hoverImg}`);

      if (!this._stack || !this._hoverImg) return;

      this._featureEls = Array.from(
        this.querySelectorAll(`[${this.attributesMap.featureHoverImage}]`)
      );

      this._featureEls.forEach((el) => {
        if (!(el instanceof HTMLElement)) return;

        el.style.cursor = 'pointer';
        el.style.touchAction = 'manipulation';

        el.addEventListener('pointerenter', this._onFeatureEnter);
        el.addEventListener('pointerleave', this._onFeatureLeave);
        el.addEventListener('pointerup', this._onFeaturePointerUp);
        el.addEventListener('click', this._onFeatureClick);
      });
    }

    disconnectedCallback() {
      this._cleanupPendingLeaveTimeout();
      this._cleanupImageListeners();

      this._featureEls.forEach((el) => {
        el.removeEventListener('pointerenter', this._onFeatureEnter);
        el.removeEventListener('pointerleave', this._onFeatureLeave);
        el.removeEventListener('pointerup', this._onFeaturePointerUp);
        el.removeEventListener('click', this._onFeatureClick);
      });

      this._featureEls = [];
      this._clickActiveEl = null;
      this._stack = null;
      this._hoverImg = null;
    }

    /**
     * @param {HTMLElement} el
     */
    _showFeatureImage(el) {
      if (!this._stack || !this._hoverImg) return;
    
      this._cleanupPendingLeaveTimeout();
      this._cleanupImageListeners();
    
      const url = el.getAttribute(this.attributesMap.featureHoverImage);
      if (!url) return;
    
      this._hoverGen += 1;
      const gen = this._hoverGen;
    
      const reveal = () => {
        if (gen !== this._hoverGen || !this._stack) return;
        this._stack.classList.add(this.classes.stackHoverActive);
      };
    
      const keepBaseVisible = () => {
        if (gen !== this._hoverGen || !this._stack) return;
        this._stack.classList.remove(this.classes.stackHoverActive);
    
        if (this._clickActiveEl === el) {
          this._clickActiveEl = null;
        }
      };
    
      if (this._hoverImg.getAttribute('src') === url && this._hoverImg.complete) {
        reveal();
        return;
      }
    
      this._hoverImg.removeAttribute('srcset');
      this._hoverImg.removeAttribute('sizes');
    
      this._imgLoadHandler = () => {
        this._cleanupImageListeners();
        reveal();
      };
    
      this._imgErrorHandler = () => {
        this._cleanupImageListeners();
        keepBaseVisible();
      };
    
      this._hoverImg.addEventListener('load', this._imgLoadHandler, { once: true });
      this._hoverImg.addEventListener('error', this._imgErrorHandler, { once: true });
    
      this._hoverImg.src = url;
    
      if (typeof this._hoverImg.decode === 'function') {
        Promise.resolve(this._hoverImg.decode())
          .then(() => {
            reveal();
          })
          .catch(() => {
            if (this._hoverImg && this._hoverImg.complete && this._hoverImg.naturalWidth > 0) {
              reveal();
            } else {
              keepBaseVisible();
            }
          });
      } else if (this._hoverImg.complete) {
        if (this._hoverImg.naturalWidth > 0) {
          reveal();
        } else {
          keepBaseVisible();
        }
      }
    }

    _hideFeatureImage() {
      if (!this._stack) return;

      this._hoverGen += 1;
      this._cleanupPendingLeaveTimeout();

      this._leaveTimeoutId = window.setTimeout(() => {
        this._leaveTimeoutId = null;

        if (this._stack) {
          this._stack.classList.remove(this.classes.stackHoverActive);
        }
      }, 32);
    }

    /**
     * @param {PointerEvent} e
     */
    _onFeatureEnter(e) {
      if (this._isTouchLikeInteraction()) return;
      if (e.pointerType && e.pointerType !== 'mouse') return;

      this._showFeatureImage(/** @type {HTMLElement} */ (e.currentTarget));
    }

    /**
     * @param {PointerEvent} e
     */
    _onFeatureLeave(e) {
      if (this._isTouchLikeInteraction()) return;
      if (e.pointerType && e.pointerType !== 'mouse') return;

      this._hideFeatureImage();
    }

    /**
     * @param {PointerEvent} e
     */
    _onFeaturePointerUp(e) {
      if (!this._isTouchLikeInteraction()) return;

      this._lastTouchInteractionAt = Date.now();
      this._handleTouchActivation(e);
    }

    _onFeatureClick(e) {
      if (!this._isTouchLikeInteraction()) return;

      if (Date.now() - this._lastTouchInteractionAt < 500) {
        e.preventDefault();
        return;
      }

      this._handleTouchActivation(e);
    }

    /**
     * @param {Event} e
     */
    _handleTouchActivation(e) {
      if (!this._stack) return;

      const el = /** @type {HTMLElement} */ (e.currentTarget);
      if (!el || !el.hasAttribute(this.attributesMap.featureHoverImage)) return;

      if (el.tagName === 'A') {
        e.preventDefault();
      }

      if (
        this._clickActiveEl === el &&
        this._stack.classList.contains(this.classes.stackHoverActive)
      ) {
        this._clickActiveEl = null;
        this._hideFeatureImage();
        return;
      }

      this._clickActiveEl = el;
      this._showFeatureImage(el);
    }
  }

  customElements.define('wls-features-image', WLSFeaturesImage);
}